import { useContext, useCallback, useEffect } from "react"
import { WebBootContext } from "source/publishing/WebBoot"
import { useJoltWithPrefetchedToken } from "source/shared/hooks/useJolt"
import dig from "lodash/get"
import { JoltContext } from "source/shared/contexts/JoltContext"
import PropTypes from "prop-types"
import { useSession } from "source/shared/hooks/useSession"
import { useQuery } from "@tanstack/react-query"
import { sessionApiClient } from "@planningcenter/cc-api-client"

export function useShouldSubscribeToPublishingJoltEvents() {
  const session = useSession()

  return Boolean(session.data.id)
}

export function ReactToPublishingJoltEvents() {
  const shouldSubscribe = useShouldSubscribeToPublishingJoltEvents()

  if (!shouldSubscribe) return null

  return <ReactToEventsWhileLoggedIn />
}

function fetchPublishingJoltToken(connectionId, currentOrgId) {
  if (!connectionId)
    throw new Error("fetchPublishingJoltToken: connectionId is required")

  return sessionApiClient.post("/publishing/v2/jolt_subscribe", {
    data: {
      attributes: {
        channel: [
          `organization-${currentOrgId}.publishing.church_center.v2.events.theme_settings`,
          `organization-${currentOrgId}.publishing.church_center.v2.events.page`,
          `organization-${currentOrgId}.publishing.church_center.v2.events.church_center_app_menu`,
          `organization-${currentOrgId}.publishing.church_center.v2.events.draft_mode`,
        ].join(","),
        connection_id: connectionId,
      },
    },
  })
}

function ReactToEventsWhileLoggedIn() {
  const { joltConnectionId, requestJoltInstance } = useContext(JoltContext)
  const {
    currentOrganization: { id: currentOrgId },
  } = useContext(WebBootContext)

  const { data: publishingJoltToken } = useQuery({
    queryKey: ["publishingJoltToken", currentOrgId, joltConnectionId],
    queryFn: () =>
      fetchPublishingJoltToken(joltConnectionId, currentOrgId).catch((error) =>
        console.error("failed to fetch publishingJoltToken", error),
      ),
    select: (data) => data?.data.id,
    enabled: !!joltConnectionId,
    staleTime: Infinity,
  })

  useEffect(() => {
    if (!publishingJoltToken) requestJoltInstance()
  }, [publishingJoltToken, requestJoltInstance])

  if (!publishingJoltToken) return null

  return (
    <SubscribeToPublishingJoltEvents
      publishingJoltToken={publishingJoltToken}
    />
  )
}

SubscribeToPublishingJoltEvents.propTypes = {
  publishingJoltToken: PropTypes.string.isRequired,
}
function SubscribeToPublishingJoltEvents({ publishingJoltToken }) {
  const {
    currentOrganization: { id: currentOrgId },
    reactToCandidateEvents,
    setDraftMode,
  } = useContext(WebBootContext)
  const { data: currentPerson } = useSession(false)

  const handleJoltEvent = useCallback(
    (e) => {
      const { data } = e.data
      reactToCandidateEvents(data)
    },
    [reactToCandidateEvents],
  )

  useJoltWithPrefetchedToken(
    publishingJoltToken,
    `organization-${currentOrgId}.publishing.church_center.v2.events.theme_settings`,
    "publishing.church_center.v2.events.theme_settings.created",
    handleJoltEvent,
  )

  useJoltWithPrefetchedToken(
    publishingJoltToken,
    `organization-${currentOrgId}.publishing.church_center.v2.events.page`,
    "publishing.church_center.v2.events.page.created",
    handleJoltEvent,
  )

  useJoltWithPrefetchedToken(
    publishingJoltToken,
    `organization-${currentOrgId}.publishing.church_center.v2.events.church_center_app_menu`,
    "publishing.church_center.v2.events.church_center_menu.created",
    handleJoltEvent,
  )

  const maybeChangeDraftMode = useCallback(
    (message) => {
      const data = message.data
      const identifier = dig(data, "data.relationships.person.data.id")
      if (identifier === currentPerson.id)
        setDraftMode(
          message.event === SubscribeToPublishingJoltEvents.draftModeStarted,
        )
    },
    [currentPerson.id, setDraftMode],
  )
  useJoltWithPrefetchedToken(
    publishingJoltToken,
    `organization-${currentOrgId}.publishing.church_center.v2.events.draft_mode`,
    SubscribeToPublishingJoltEvents.draftModeEvents,
    maybeChangeDraftMode,
  )

  return null
}
SubscribeToPublishingJoltEvents.draftModeStarted =
  "publishing.church_center.v2.events.draft_mode.started"
SubscribeToPublishingJoltEvents.draftModeFinished =
  "publishing.church_center.v2.events.draft_mode.finished"
SubscribeToPublishingJoltEvents.draftModeEvents = [
  SubscribeToPublishingJoltEvents.draftModeStarted,
  SubscribeToPublishingJoltEvents.draftModeFinished,
]
